import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserTree } from '@skill-trees/shared';
import { RenameTreeModal } from '../tree-management/RenameTreeModal';
import { DeleteTreeModal } from '../tree-management/DeleteTreeModal';
import { CreateTreeModal } from '../tree-management/CreateTreeModal';
import { PlusIcon } from '../ui/icons';

interface TreeListProps {
  trees: UserTree[];
  isLoading: boolean;
  error: string | null;
  onRename: (treeId: string, newName: string) => Promise<void>;
  onDelete: (treeId: string) => Promise<void>;
  onToggleVisibility: (treeId: string) => Promise<void>;
  onRetry: () => void;
}

export const TreeList: React.FC<TreeListProps> = ({
  trees,
  isLoading,
  error,
  onRename,
  onDelete,
  onToggleVisibility,
  onRetry
}) => {
  const navigate = useNavigate();
  const [renameModalOpen, setRenameModalOpen] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const [selectedTree, setSelectedTree] = React.useState<UserTree | null>(null);
  const [hoveredTreeId, setHoveredTreeId] = React.useState<string | null>(null);

  const handleTreeClick = (tree: UserTree) => {
    navigate(`/tree/${tree.slug}/edit`);
  };

  // Touch fires mouseenter with no matching mouseleave, so after tapping an action
  // the row's overlay would stay stuck; drop the hover state and focus there.
  // Hover-capable devices keep both — the cursor is still over the row.
  const clearHoverAfterTouchAction = () => {
    if (typeof window.matchMedia !== 'function' || !window.matchMedia('(hover: none)').matches) return;
    setHoveredTreeId(null);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const handleRenameClick = (e: React.MouseEvent, tree: UserTree) => {
    e.stopPropagation();
    clearHoverAfterTouchAction();
    setSelectedTree(tree);
    setRenameModalOpen(true);
  };

  const handleDeleteClick = (e: React.MouseEvent, tree: UserTree) => {
    e.stopPropagation();
    clearHoverAfterTouchAction();
    setSelectedTree(tree);
    setDeleteModalOpen(true);
  };

  const handleToggleVisibilityClick = async (e: React.MouseEvent, tree: UserTree) => {
    e.stopPropagation();
    clearHoverAfterTouchAction();
    try {
      await onToggleVisibility(tree.slug);
    } catch (err) {
      console.error('Failed to toggle visibility:', err);
    }
  };

  const handleRename = async (treeId: string, newName: string) => {
    await onRename(treeId, newName);
    setRenameModalOpen(false);
    setSelectedTree(null);
  };

  const handleDelete = async (treeId: string) => {
    await onDelete(treeId);
    setDeleteModalOpen(false);
    setSelectedTree(null);
  };

  const createTreeButton = (
    <button
      type="button"
      className="btn btn-primary tree-list-create-button"
      onClick={() => setCreateModalOpen(true)}
    >
      Create Tree
      <PlusIcon />
    </button>
  );

  const createTreeModal = (
    <CreateTreeModal isOpen={createModalOpen} onClose={() => setCreateModalOpen(false)} />
  );

  if (isLoading) {
    // Placeholder cards matching the list design while trees load.
    return (
      <div className="tree-list-container" aria-busy="true" aria-label="Loading your trees">
        {[0, 1, 2].map((i) => (
          <div key={i} className="tree-list-item tree-list-item-skeleton" aria-hidden="true">
            <div className="tree-list-item-content">
              <div className="tree-list-skeleton-bar tree-list-skeleton-name" />
              <div className="tree-list-skeleton-bar tree-list-skeleton-id" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="tree-list-error">
        <p className="error-message">{error}</p>
        <button className="btn btn-primary" onClick={onRetry}>
          Retry
        </button>
      </div>
    );
  }

  if (trees.length === 0) {
    return (
      <div className="tree-list-empty">
        <p>You haven&apos;t created any trees yet.</p>
        {createTreeButton}
        {createTreeModal}
      </div>
    );
  }

  return (
    <>
      <div className="tree-list-container">
        {trees.map((tree) => (
          <div
            key={tree.id}
            className={`tree-list-item ${hoveredTreeId === tree.id ? 'tree-list-item-actions-visible' : ''}`}
            onClick={() => handleTreeClick(tree)}
            onMouseEnter={() => setHoveredTreeId(tree.id)}
            onMouseLeave={() => setHoveredTreeId(null)}
          >
            <div className="tree-list-item-content">
              <div className="tree-list-item-name">
                {tree.name}
                {tree.isPrivate && (
                  <img
                    src="/icons/padlock.png"
                    alt="Private tree"
                    className="tree-list-privacy-icon"
                  />
                )}
              </div>
              <div className="tree-list-item-id">{tree.slug}</div>
            </div>
            <div
              className={`tree-list-actions ${hoveredTreeId === tree.id ? 'tree-list-actions-visible' : ''}`}
            >
              <button
                className="tree-list-action"
                onClick={(e) => handleRenameClick(e, tree)}
                title="Rename"
              >
                Rename
              </button>
              <button
                className="tree-list-action"
                onClick={(e) => handleToggleVisibilityClick(e, tree)}
                title={tree.isPrivate ? 'Publish' : 'Make Private'}
              >
                {tree.isPrivate ? 'Publish' : 'Make Private'}
              </button>
              <button
                className="tree-list-action tree-list-action-danger"
                onClick={(e) => handleDeleteClick(e, tree)}
                title="Delete"
              >
                Delete
              </button>
            </div>
          </div>
        ))}

        {createTreeButton}
      </div>

      {createTreeModal}

      {selectedTree && (
        <>
          <RenameTreeModal
            isOpen={renameModalOpen}
            initialName={selectedTree.name}
            onClose={() => {
              setRenameModalOpen(false);
              setSelectedTree(null);
            }}
            onConfirm={(value)=>{handleRename(selectedTree.slug, value)}}
          />
          <DeleteTreeModal
            isOpen={deleteModalOpen}
            treeId={selectedTree.slug}
            treeName={selectedTree.name}
            onClose={() => {
              setDeleteModalOpen(false);
              setSelectedTree(null);
            }}
            onDelete={handleDelete}
          />
        </>
      )}
    </>
  );
};

